import { cn } from '@/lib/utils';
import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: 'text' | 'outline' | 'contained';
	size?: 'small' | 'medium' | 'large';
	startIcon?: ReactNode;
	endIcon?: ReactNode;
	text: string;
	imageBackgroundUrl?: string;
	className?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			variant = 'contained',
			size = 'medium',
			startIcon,
			endIcon,
			text,
			className,
			imageBackgroundUrl,
			style,
			...props
		},
		ref,
	) => {
		const baseClasses =
			'disabled:cursor-not-allowed focus:outline-none box-border rounded font-normal transition-colors flex items-center gap-1 cursor-pointer py-1 whitespace-nowrap';

		const variantClasses = {
			contained: 'text-light bg-cyan-950 text-sm',
			outline:
				'border border-zinc-200 border-solid text-zinc-600 hover:bg-cyan-800/10',
			text: 'text-dark hover:bg-cyan-800/10',
		};

		const sizeClasses = {
			small: ' px-2 text-sm',
			medium: 'px-4 text-base',
			large: 'w-full px-6 text-base',
		};

		const mergedClasses = cn(
			baseClasses,
			variant && variantClasses[variant],
			size && sizeClasses[size],
			className,
			{
				'bg-cover bg-center bg-no-repeat': imageBackgroundUrl,
			},
		);

		const mergedStyles = {
			...style,
			...(imageBackgroundUrl
				? { backgroundImage: `url(${imageBackgroundUrl})` }
				: {}),
		};

		return (
			<button
				ref={ref}
				className={mergedClasses}
				style={mergedStyles}
				{...props}
			>
				{startIcon && startIcon}
				{text}
				{endIcon && endIcon}
			</button>
		);
	},
);

Button.displayName = 'Button';

export { Button };
